// /teams/:teamId/projects/:projectId/runs/:runId — live step-by-step view.
//
// Top section: status banner with flow name, env, model, duration. Click
// "Re-run" to enqueue a new run-test job against the same flow + env.
// Body: FlowViewer (static) on the left, RunStepList (live) on the right.

import { useState } from 'react'
import { useAuthState } from 'react-firebase-hooks/auth'
import { useParams } from 'react-router-dom'
import { FlowViewer } from '../../components/dashboard/FlowViewer.jsx'
import { PageHeader } from '../../components/dashboard/PageHeader.jsx'
import { RunStepList } from '../../components/dashboard/RunStepList.jsx'
import { Button } from '../../components/ui/button.jsx'
import { useFlows, useProject, useRun, useRunSteps } from '../../lib/data-hooks.js'
import { auth } from '../../lib/firebase.js'
import { enqueueJob } from '../../lib/jobs-api.js'

const STATUS_STYLES = {
  passed: 'border-[var(--color-passed)] bg-[var(--color-passed)]/10 text-[var(--color-passed)]',
  failed: 'border-[var(--color-failed)] bg-[var(--color-failed)]/10 text-[var(--color-failed)]',
  running: 'border-[var(--color-running)] bg-[var(--color-running)]/10 text-[var(--color-running)]',
}

function fmtDuration(ms) {
  if (!ms) return null
  if (ms < 1000) return `${ms}ms`
  return `${(ms / 1000).toFixed(2)}s`
}

function fmtTime(t) {
  if (!t) return ''
  const ms = typeof t === 'string' ? new Date(t).getTime() : (t.toMillis?.() ?? Number(t))
  if (!ms) return ''
  return new Date(ms).toLocaleString()
}

export function RunDetail() {
  const { teamId, projectId, runId } = useParams()
  const [user] = useAuthState(auth)
  const { data: project } = useProject(teamId, projectId)
  const { data: run, loading: runLoading } = useRun(teamId, projectId, runId)
  const {
    data: steps,
    loading: stepsLoading,
    error: stepsError,
  } = useRunSteps(teamId, projectId, runId)
  const { data: flows } = useFlows(teamId, projectId)

  const matchingFlow = flows.find((f) => f.name === run?.flowName || f.id === run?.flowId)

  const [reRunBusy, setReRunBusy] = useState(false)
  const [reRunError, setReRunError] = useState(null)
  const [reRunResult, setReRunResult] = useState(null)

  async function handleReRun() {
    if (!run || !matchingFlow?.yaml || !user?.uid) return
    setReRunBusy(true)
    setReRunError(null)
    setReRunResult(null)
    try {
      const traceId = `trace_${Date.now()}`
      const idempotencyKey = `rerun_${traceId}_${Math.random().toString(36).slice(2, 10)}`
      const result = await enqueueJob({
        type: 'run-test',
        traceId,
        idempotencyKey,
        teamId,
        userId: user.uid,
        graphVersion: 'v1alpha1',
        payload: {
          flowYaml: matchingFlow.yaml,
          flowName: run.flowName,
          envName: run.env ?? 'dev',
          projectId,
        },
      })
      setReRunResult(result)
    } catch (err) {
      setReRunError(err.message)
    } finally {
      setReRunBusy(false)
    }
  }

  const statusCls =
    STATUS_STYLES[run?.status] ??
    'border-[var(--color-sandstone)] bg-[var(--color-sandstone)]/40 text-[var(--color-slate)]'

  return (
    <>
      <PageHeader
        breadcrumb={[
          { label: 'Teams', to: '/teams' },
          { label: teamId, to: `/teams/${teamId}` },
          { label: project?.name ?? projectId, to: `/teams/${teamId}/projects/${projectId}` },
          { label: 'Runs', to: `/teams/${teamId}/projects/${projectId}/runs` },
          { label: run?.flowName ?? runId },
        ]}
        title={run?.flowName ?? runId}
        subhead={run ? `${run.env ?? 'dev'} · ${fmtTime(run.startedAt)}` : 'Loading…'}
        actions={
          <Button
            type="button"
            variant="primary"
            size="sm"
            disabled={reRunBusy || !matchingFlow?.yaml || !user?.uid}
            onClick={handleReRun}
          >
            {reRunBusy ? 'Enqueuing…' : 'Re-run'}
          </Button>
        }
      />

      <div className="mx-auto max-w-6xl px-6 py-6">
        <div
          className={`mb-6 flex flex-wrap items-center justify-between gap-3 rounded-md border px-5 py-3 ${statusCls}`}
        >
          <div className="flex items-center gap-3">
            <span className="font-mono text-sm uppercase tracking-wider">
              {run?.status ?? 'loading'}
            </span>
            {run?.durationMs && (
              <span className="font-mono text-xs">{fmtDuration(run.durationMs)}</span>
            )}
          </div>
          <div className="flex items-center gap-3 text-xs">
            {run?.model && <span className="font-mono">{run.model}</span>}
            {run?.runId && (
              <span className="font-mono text-[var(--color-schist)]">{run.runId}</span>
            )}
          </div>
        </div>

        {reRunError && (
          <p className="mb-4 text-sm text-[var(--color-failed)]" role="alert">
            Re-run failed: {reRunError}
          </p>
        )}
        {reRunResult && (
          <p className="mb-4 text-sm text-[var(--color-schist)]">
            Re-run queued — new run: <span className="font-mono">{reRunResult.jobId}</span>
          </p>
        )}

        <div className="grid gap-6 lg:grid-cols-[1fr_minmax(420px,560px)]">
          <div className="h-[520px] overflow-hidden rounded-md border border-[var(--color-sandstone)] bg-[var(--color-limestone)]">
            {matchingFlow?.yaml ? (
              <FlowViewer yaml={matchingFlow.yaml} />
            ) : (
              <p className="px-4 py-3 text-sm text-[var(--color-schist)]">
                {runLoading
                  ? 'Loading flow…'
                  : 'The flow this run executed is no longer in the project (or never had its YAML mirrored to Firestore).'}
              </p>
            )}
          </div>
          <div className="rounded-md border border-[var(--color-sandstone)] bg-[var(--color-limestone)]">
            <RunStepList steps={steps} loading={stepsLoading} error={stepsError} />
          </div>
        </div>
      </div>
    </>
  )
}
